import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { X, MagnifyingGlass } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function MountTireModal({ truckId, position, onClose, onComplete }) {
  const { profile } = useAuth();
  const [tires, setTires] = useState([]);
  const [filteredTires, setFilteredTires] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTire, setSelectedTire] = useState(null);
  const [odometer, setOdometer] = useState('');
  const [mountDate, setMountDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [lifetimeKm, setLifetimeKm] = useState({});

  const todayStr = new Date().toISOString().split('T')[0];
  const minDate = selectedTire?.purchase_date
    ? new Date(selectedTire.purchase_date).toISOString().split('T')[0]
    : undefined;

  useEffect(() => { fetchInStockTires(); }, []);

  useEffect(() => {
    if (searchTerm) {
      setFilteredTires(tires.filter(t => t.serial_number.toLowerCase().includes(searchTerm.toLowerCase())));
    } else { setFilteredTires(tires); }
  }, [searchTerm, tires]);

  const fetchInStockTires = async () => {
    try {
      const { data, error } = await supabase
        .from('tires').select('*')
        .eq('company_id', profile.company_id).eq('status', 'stock')
        .order('serial_number');
      if (error) throw error;
      setTires(data || []);
      setFilteredTires(data || []);

      if (data && data.length > 0) {
        const ids = data.map(t => t.id);
        const { data: history } = await supabase
          .from('tire_positions').select('tire_id, mounted_km, dismounted_km').in('tire_id', ids);
        const km = {};
        (history || []).forEach(p => {
          if (p.mounted_km != null && p.dismounted_km != null) {
            km[p.tire_id] = (km[p.tire_id] || 0) + (p.dismounted_km - p.mounted_km);
          }
        });
        setLifetimeKm(km);
      }
    } catch (error) {
      console.error('Error fetching tires:', error);
      toast.error('Gagal memuat ban tersedia');
    }
  };

  const handleMount = async () => {
    if (!selectedTire || !odometer) {
      toast.error('Pilih ban dan masukkan odometer');
      return;
    }
    if (!mountDate) {
      toast.error('Pilih tanggal pemasangan');
      return;
    }
    if (mountDate > todayStr) {
      toast.error('Tanggal tidak boleh di masa depan');
      return;
    }
    if (minDate && mountDate < minDate) {
      toast.error(`Tanggal tidak boleh sebelum tanggal masuk ban (${new Date(minDate).toLocaleDateString('id-ID')})`);
      return;
    }
    setLoading(true);
    try {
      const mountIso = new Date(`${mountDate}T12:00:00`).toISOString();
      const { data: updated, error: updateErr } = await supabase
        .from('tire_positions').update({
          tire_id: selectedTire.id,
          mounted_km: parseInt(odometer),
          mounted_date: mountIso,
          tread_depth_on: selectedTire.tread_depth,
        })
        .eq('truck_id', truckId)
        .eq('position_number', position.position_number)
        .is('dismounted_date', null).is('tire_id', null).select();
      if (updateErr) throw updateErr;

      if (!updated || updated.length === 0) {
        const { error: insertErr } = await supabase.from('tire_positions').insert([{
          truck_id: truckId,
          position_number: position.position_number,
          tire_id: selectedTire.id,
          mounted_km: parseInt(odometer),
          mounted_date: mountIso,
          tread_depth_on: selectedTire.tread_depth,
        }]);
        if (insertErr) throw insertErr;
      }

      const { error: tireError } = await supabase.from('tires').update({ status: 'mounted' }).eq('id', selectedTire.id);
      if (tireError) throw tireError;

      toast.success(`Ban ${selectedTire.serial_number} dipasang di posisi ${position.position_number}`);
      onComplete();
    } catch (error) {
      console.error('Error mounting tire:', error);
      toast.error(error.message || 'Gagal memasang ban');
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="mount-modal">
      <div className="bg-white border border-zinc-300 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="flex items-center justify-between p-6 border-b border-zinc-200">
          <h2 className="text-xl sm:text-2xl font-black uppercase tracking-wider text-zinc-900" style={{ fontFamily: 'Chivo' }}>
            PASANG BAN — POSISI {position.position_number}
          </h2>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-900" data-testid="close-mount-modal">
            <X size={24} weight="bold" />
          </button>
        </div>

        <div className="p-6">
          <div className="mb-6">
            <label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 block mb-2">Cari Ban di Stok</label>
            <div className="relative">
              <MagnifyingGlass size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Cari berdasarkan nomor seri..."
                className="w-full bg-white border border-zinc-300 p-3 pl-10 text-zinc-900 font-mono focus:border-red-500 focus:ring-1 focus:ring-red-500 focus:outline-none"
                data-testid="tire-search-input" />
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto border border-zinc-200 mb-6">
            {filteredTires.length === 0 ? (
              <p className="p-6 text-center text-zinc-500 font-mono text-sm">Tidak ada ban di stok</p>
            ) : (
              filteredTires.map((tire) => {
                const km = lifetimeKm[tire.id] || 0;
                return (
                  <button key={tire.id} onClick={() => setSelectedTire(tire)}
                    className={`w-full p-4 text-left border-b border-zinc-200 transition-colors ${
                      selectedTire?.id === tire.id ? 'bg-red-600 text-white' : 'hover:bg-zinc-50 text-zinc-700'
                    }`}
                    data-testid={`select-tire-${tire.serial_number}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-mono text-sm font-bold">{tire.serial_number}</p>
                        <p className="font-mono text-xs opacity-75">{tire.type} | {tire.size}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-mono text-xs">{tire.tread_depth}mm</p>
                        {km > 0 && <p className="font-mono text-[10px] opacity-75">Bekas · {km.toLocaleString('id-ID')} KM</p>}
                        {km === 0 && <p className="font-mono text-[10px] opacity-75">Baru</p>}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          <div className="mb-6">
            <label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 block mb-2">Odometer Saat Ini (KM) *</label>
            <input type="number" value={odometer} onChange={(e) => setOdometer(e.target.value)}
              placeholder="Masukkan KM saat ini"
              className="w-full bg-white border border-zinc-300 p-3 text-zinc-900 font-mono focus:border-red-500 focus:ring-1 focus:ring-red-500 focus:outline-none"
              data-testid="odometer-input" />
          </div>

          <div className="mb-6">
            <label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 block mb-2">
              Tanggal Pemasangan *
            </label>
            <input type="date" value={mountDate} onChange={(e) => setMountDate(e.target.value)}
              max={todayStr}
              min={minDate}
              className="w-full bg-white border border-zinc-300 p-3 text-zinc-900 font-mono focus:border-red-500 focus:ring-1 focus:ring-red-500 focus:outline-none"
              data-testid="mount-date-input" />
            <p className="text-[10px] font-mono text-zinc-500 mt-1">
              Default hari ini. Tanggal masa depan tidak diizinkan.
              {minDate && ` Minimum: ${new Date(minDate).toLocaleDateString('id-ID')} (tanggal masuk).`}
            </p>
          </div>

          <div className="flex gap-3">
            <button onClick={handleMount} disabled={loading || !selectedTire || !odometer}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white font-mono uppercase text-xs tracking-wider py-3 px-6 transition-colors disabled:opacity-50"
              data-testid="confirm-mount-button">
              {loading ? 'MEMASANG...' : 'KONFIRMASI PASANG'}
            </button>
            <button onClick={onClose}
              className="border border-zinc-300 hover:bg-zinc-100 text-zinc-900 font-mono uppercase text-xs tracking-wider py-3 px-6 transition-colors"
              data-testid="cancel-mount-button">
              BATAL
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
